import React, { useCallback, useEffect, useMemo, useState } from "react";
import NewFilterContainer from "../../../../components/NewFilterContainer ";
import MainPageContainer from "../../../../components/MainPageContainer";
import SecondaryHeader from "../../../Rajasthan/Demand/SecondaryHeader";
import Loading from "../../../../components/Loading";
import { useSelector } from "react-redux";
import { getByExchangeData } from "../../../../Rest_api/RestApi";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../components/recharts/functions";
import { getMonthFromDate } from "../../../../extensions/date";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import { renderHourTick } from "../../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../../components/table/SummaryTable";
import { BrushStart, ChartType } from "../../../../models/chart_model";
import { AddDatesWithFileName } from "../../../../helpers/helper";
import FilterMenu from "../../../../components/FilterMenu";
import { ExchangeDataTabs } from "../../../Rajasthan/Demand/Data";
import BlockRangeSelector from "../../../../components/BlockRangeSelector/BlockRangeSelector";

interface ExchangeDataItem {
  dateIndex: string;
  date: string;
  exchange__name: string;
  mcp_rs_mwh: number;
  mcv_mw: number;
  product__name: string;
  purchase_bid_mw: number;
  sell_bid_mw: number;
  time_slot: number;
  wt_mcp_rs_mwh: number;
}

const chartSuffixes = [
  {
    suffix: "wt_mcp_rs_mwh",
    displayName: "MCP",
    stackSuffix: "wt_mcp_rs_mwh",
    chartType: ChartType.Line,
    unit: "Rs/KWh",
    yAxisId: "right",
  },
  {
    suffix: "sell_bid",
    displayName: "Sell Bid",
    stackSuffix: "sell_bid",
    chartType: ChartType.Line,
    unit: "MW/MWh",
    yAxisId: "left",
  },
  {
    suffix: "purchase_bid",
    displayName: "Purchase Bid",
    stackSuffix: "purchase_bid",
    chartType: ChartType.Line,
    unit: "MW/MWh",
    yAxisId: "left",
  },
  {
    suffix: "mcv",
    displayName: "MCV",
    stackSuffix: "mcv",
    chartType: ChartType.Bar,
    unit: "MW/MWh",
    yAxisId: "left",
  },
];

let Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["time_slot", "dateIndex"],
    isTime: true,
  },
  {
    label: "Date",
    key: "dateIndex",
    values: ["dateIndex"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const exchanges = ["IEX", "PXIL", "HPX"];
const UNIT_OPTIONS = ["MW", "MWh"];

const TrendAnalysis = () => {
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [chartData, setChartData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedUnit, setSelectedUnit] = useState(UNIT_OPTIONS[0]);
  const [selectedMetrics, setSelectedMetrics] = useState<any[]>([
    chartSuffixes[0],
    chartSuffixes[1],
    chartSuffixes[2],
    chartSuffixes[3],
  ]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ExchangeDataItem[]>([]);
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>([
    exchanges[0],
  ]);
  const [product, setProduct] = useState<string[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string[]>([]);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultTrendDates === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultTrendDates;
    const promises = dates.map(async (date: any) => {
      const [startDate, endDate] = date.value.split(",");
      const newData = await getByExchangeData(startDate, endDate);
      newData.data.sort((a: ExchangeDataItem, b: ExchangeDataItem) => {
        if (a.date === b.date) {
          return a.time_slot - b.time_slot;
        }
        return a.date.localeCompare(b.date);
      });
      const uniqueDates = Array.from(
        new Set(newData?.data?.map((item: { date: any }) => item.date))
      );
      return newData?.data?.map((item: any) => ({
        ...item,
        dates: date.label,
        dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
      }));
    });
    const results = await Promise.all(promises);
    const data = results.flat();

    setData(data);
    setUniqueBlock(
      Array.from(new Set(data.map((item: ExchangeDataItem) => item.time_slot)))
    );
    const products = Array.from(
      new Set(data.map((item: ExchangeDataItem) => item.product__name))
    );
    setProduct(products as string[]);
    setSelectedProduct([String(products[2])]);
    setLoading(false);
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const handleKey = useCallback(() => {
    if (selectedBlock.length > 0) {
      if (selectedInterval.key === "time") {
        return ["dateIndex", "time_slot"];
      } else if (selectedInterval.key === "dateIndex") {
        return ["dateIndex"];
      } else if (selectedInterval.key === "month") {
        return ["month"];
      }
    } else {
      return selectedInterval.values;
    }
  }, [selectedBlock.length, selectedInterval.key, selectedInterval.values]);

  useEffect(() => {
    let filteredData = data.map((item) => ({
      ...item,
      month: getMonthFromDate(item.date),
      wt_mcp_rs_mwh: item.wt_mcp_rs_mwh / 1000,
      sell_bid_mw: item.sell_bid_mw,
      purchase_bid_mw: item.purchase_bid_mw,
      mcv_mw: item.mcv_mw,
      sell_bid_mwh: item.sell_bid_mw / 4,
      purchase_bid_mwh: item.purchase_bid_mw / 4,
      mcv_mwh: item.mcv_mw / 4,
    }));

    if (selectedExchanges.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedExchanges.includes(item.exchange__name)
      );
    }

    if (selectedProduct.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedProduct.includes(item.product__name)
      );
    }

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.time_slot)
      );
    }

    const aggregationKeys = selectedMetrics.map((metric) => {
      if (metric.unit === "MW/MWh") {
        return `${metric.suffix}_${selectedUnit.toLowerCase()}`;
      }
      return metric.suffix;
    });

    const par: ChartDataParams = {
      keys: handleKey() || [],
      tableKeys: ["date", "time_slot", "month"],
      groupBy: ["dates", "exchange__name", "product__name"],
      volumeKey: ``,
      aggregationKeys: aggregationKeys,
      priceKey: "wt_mcp_rs_kwh",
      data: filteredData,
    };

    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [
    data,
    handleKey,
    selectedBlock,
    selectedExchanges,
    selectedInterval.key,
    selectedInterval.values,
    selectedMetrics,
    selectedProduct,
    selectedUnit,
  ]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: string,
    unit: string,
    yAxisId: string
  ) => {
    let actualUnit = unit;
    if (unit === "MW/MWh") {
      actualUnit = selectedUnit;
    }

    return {
      dataKey: `${key}_${suffix}`,
      name: `${key.replace("_", " ").replace("undefined", "")} ${displayName}`,
      legendColor: undefined,
      type: chartType,
      unit: actualUnit,
      yAxisId: yAxisId,
      stackId: stackSuffix !== undefined ? stackSuffix : undefined,
    };
  };

  function getLegends() {
    return selectedMetrics.flatMap(
      ({ suffix, displayName, stackSuffix, chartType, unit, yAxisId }) =>
        chartKeys.map((key, index) => {
          let actualSuffix = suffix;
          if (unit === "MW/MWh") {
            actualSuffix = `${suffix}_${selectedUnit.toLowerCase()}`;
          }

          return createChartObject(
            key,
            index,
            actualSuffix,
            displayName,
            stackSuffix,
            chartType,
            unit,
            yAxisId
          ) as any;
        })
    );
  }

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") {
      if (selectedBlock.length > 0) {
        return "time_slot";
      }
      return "time_slot";
    } else {
      return selectedInterval.key;
    }
  }, [selectedBlock, selectedInterval]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const summaryExtraKeys = selectedInterval.isTime
    ? ["time_slot", selectedInterval.values[1]]
    : [selectedInterval.values[0]];

  const summaryExtraLabels = selectedInterval.values.map((value) =>
    value === "date" ? "Date" : value
  );

  const tableUnits: Record<string, string> = chartSuffixes.reduce(
    (acc, curr) => {
      let actualUnit = curr.unit;
      if (curr.unit === "MW/MWh") {
        actualUnit = selectedUnit;
      }
      return { ...acc, [curr.suffix]: actualUnit };
    },
    {}
  );

  const tablePar = {
    legends: getLegends(),
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Exchange Overview",
      dates: DefaultDates[activeTab].defaultTrendDates,
    }),
    customUnits: tableUnits,
  };

  function secondDataKey() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        return "dateIndex";
      }
      return "dateIndex";
    } else {
      return undefined;
    }
  }

  return (
    <>
      <div className="gas-page-container">
        {loading && <Loading />}
        <NewFilterContainer>
          <div className="side-filters-container text-nowrap">
            <FilterMenu
              title="Intervals"
              items={Intervals.map((item) => item.label)}
              selectedItems={[selectedInterval.label]}
              onSelect={(item, index) => {
                setSelectedInterval(Intervals[index]);
              }}
            />
            <FilterMenu
              title="Unit"
              items={UNIT_OPTIONS}
              selectedItems={[selectedUnit]}
              onSelect={(item, index) => {
                setSelectedUnit(UNIT_OPTIONS[index]);
              }}
            />
            <FilterMenu
              title="Exchanges"
              items={exchanges.map((item) => item)}
              selectedItems={selectedExchanges}
              setSelectedItems={setSelectedExchanges}
            />
            <FilterMenu
              title="Products"
              items={product.map((item) => item)}
              selectedItems={selectedProduct}
              setSelectedItems={setSelectedProduct}
            />

            <FilterMenu
              title="Metrics"
              items={chartSuffixes.map((item) => item.displayName)}
              selectedItems={selectedMetrics.map((item) => item.displayName)}
              setSelectedItems={(selectedItems: string[]) => {
                const newSelectedMetrics = chartSuffixes.filter((item) =>
                  selectedItems.includes(item.displayName)
                );
                setSelectedMetrics(newSelectedMetrics);
              }}
            />
          </div>
        </NewFilterContainer>

        <MainPageContainer>
          <SecondaryHeader ExchangeDataTabs={ExchangeDataTabs} />
          <div className="gas-chart-container">
            <div className="gas-heading gap-x-10">
              <h2 className="chartHeading w-60">Trend Analysis</h2>
              <div className="flex gap-x-6 justify-end">
                <BlockRangeSelector
                  blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                  placeholder="Select Slot"
                  selectedOptions={selectedBlock.map((item) => item.value)}
                  setSelectedOptions={setSelectedBlock}
                />
                {/* <button
                  onClick={() => {
                    dataTab.label === "Chart" &&
                      downloadChartTableCSV(tablePar);
                    dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                    dataTab.label === "Table" &&
                      downloadChartTableCSV(tablePar);
                  }}
                  className="downloadButton"
                >
                  Download Data
                </button> */}
              </div>
            </div>

            {dataTab.label === "Chart" && (
              <ReMixChart
                onBarClick={(item: any) => {
                  console.log(item);
                }}
                data={chartData}
                showBrush={chartData.length > 96 ? true : undefined}
                brushStart={BrushStart.Start}
                isTimeSlot={selectedInterval.isTime}
                onlyTitle={true}
                legends={getLegends()}
                secondYAxisLabel="Rs/KWh"
                yAxisLabel={selectedUnit}
                xDataKey={xDataKey}
                xTick={
                  selectedInterval.isTime && selectedBlock.length === 0
                    ? (value: any, index: any) => {
                        return renderHourTick(value, 10);
                      }
                    : undefined
                }
                secondXDataKey={secondDataKey()}
              />
            )}
            {dataTab.label === "Summary" && (
              <LegendSummaryTable {...tablePar} />
            )}
            {/* {dataTab.label === "Table" && <ChartTable {...tablePar} />} */}
          </div>
          <div className="gna-footer-container">
            <div className="grid grid-cols-2 justify-between gap-5 ">
              <p className="gna-footer-text col-span-1">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>
    </>
  );
};

export default TrendAnalysis;